import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// Initialize Sentry as early as possible
Sentry.init({
  dsn: "https://bd1efa307abd3725427f4ff2ee6a3628@o4509322028908544.ingest.de.sentry.io/4510707675234384",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // Enable React integration
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of the transactions for performance monitoring
  // Session Replay
  replaysSessionSampleRate: 0.1, // Sample 10% of sessions for replay
  replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
  // Environment
  environment: import.meta.env.MODE || "development",
  // Debug mode (only in development)
  debug: false,
  // Capture console errors
  beforeSend(event, hint) {
    // Log to console in development for debugging
    if (import.meta.env.MODE === 'development') {
      console.log('🔴 Sentry Event Captured:', event);
      console.log('📋 Event Details:', hint);
    }
    return event;
  },
});

// Expose Sentry to window for console testing (available in all environments)
(window as any).Sentry = Sentry;
if (import.meta.env.MODE === "development") {
  console.log('✅ Sentry is available in console. Use: Sentry.captureException(new Error("test"))');
}

// Chrome's built-in page translation rewrites text nodes into <font> elements,
// which breaks React 18's DOM bookkeeping and crashes the whole tree with
// "Failed to execute 'removeChild'/'insertBefore' on 'Node'" (facebook/react#11538,
// fixed in React 19). Tolerate the mismatch instead of unmounting the app.
if (typeof Node !== "undefined") {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (this: Node, child: Node) {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child);
  } as typeof Node.prototype.removeChild;

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (this: Node, newNode: Node, referenceNode: Node | null) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  } as typeof Node.prototype.insertBefore;
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}

const root = createRoot(container);
root.render(<App />);
